import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useRedeemEventPassMutation } from '@/generated/graphql';
import { useAdminScreenThemeState } from '@/providers/AdminScreenThemeStateProvider/AdminScreenThemeStateContext';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

export function ReturnFromStripeDialog() {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [eventPassIdFromUrl, setEventPassIdFromUrl] = useState<string | null>(
    null,
  );
  const [isSubscription, setIsSubscription] = useState<boolean | null>(null);
  const { event } = useAdminScreenThemeState();
  const [redeemEventPass] = useRedeemEventPassMutation();
  const [searchParams, setSearchParams] = useSearchParams();

  // handle event pass payment cancelled
  useEffect(() => {
    const transactionCancelled =
      searchParams.get('eventPassCancelled') === 'true';

    if (transactionCancelled) {
      toast.error('Transaction cancelled');

      // clean up the URL
      searchParams.delete('eventPassCancelled');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // handle event pass payment success
  useEffect(() => {
    const eventPassId = searchParams.get('eventPass');
    if (eventPassId) {
      setOpen(true);
      setEventPassIdFromUrl(eventPassId);

      // clean up the URL
      searchParams.delete('eventPass');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // handle subscription payment success
  useEffect(() => {
    const subscription = searchParams.get('subscription');
    if (subscription === 'true') {
      setIsSubscription(true);
      setOpen(true);

      // clean up the URL
      searchParams.delete('subscription');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const dialogTitle =
    isSubscription === true
      ? 'You purchased a subscription!'
      : 'You purchased an event pass!';
  const dialogDescription = `Do you want to set ${event.name} to live with it?`;

  const handleRedeemClick = () => {
    if (!eventPassIdFromUrl) {
      toast.error('Event pass not found');
      return;
    }

    setPending(true);
    redeemEventPass({
      variables: {
        eventPassId: eventPassIdFromUrl,
        eventId: event.id,
      },
    })
      .then(({ data }) => {
        if (data?.redeemEventPass?.eventPassRedemption) {
          setOpen(false);
        } else {
          throw new Error('Failed to redeem event pass');
        }
      })
      .catch((e) => {
        toast.error(e.message);
      })
      .finally(() => {
        setPending(false);
      });
  };
  const handleUseSubscription = () => {
    setPending(true);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {eventPassIdFromUrl && (
            <Button
              size="xl"
              disabled={pending}
              variant="constructive"
              onClick={handleRedeemClick}
            >
              📣 Make&nbsp;<strong>{event.name}</strong>&nbsp;live now!
            </Button>
          )}
          {isSubscription === true && (
            <Button
              size="xl"
              variant="constructive"
              disabled={pending}
              onClick={handleUseSubscription}
            >
              📣 Make&nbsp;<strong>{event.name}</strong>&nbsp;live now!
            </Button>
          )}
          <Button
            size="xl"
            disabled={pending}
            variant="outline"
            onClick={() => setOpen(false)}
          >
            ⏱️ I'll use it later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
